/**
 * PathwayBreadcrumb (Sprint 4.3.1, PROD-650).
 *
 * Drill-down breadcrumb for the Awell 3-tier hierarchy. Renders the
 * path `Pathway > Track > Step > Action` as the user descends through
 * the canvas zoom levels. Each crumb is a real button so keyboard +
 * screen-reader users can hop levels without using the mouse wheel.
 *
 * Reference: `awell/studio/docs/getting-started/design-ontology.md`.
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: `<nav>` with
 * `aria-label` + each crumb is a focusable button.
 */
import type { HierarchyLevel } from '../../services/pathway-layout';

export interface PathwayBreadcrumbCrumb {
  /** Label rendered for the crumb (e.g. "Track: Onboarding"). */
  label: string;
  /** Hierarchy level the crumb maps to. */
  level: HierarchyLevel | 'pathway';
  /** Optional id of the focused entity at this level. */
  nodeId?: string | null;
}

export interface PathwayBreadcrumbProps {
  pathwayLabel: string;
  trackLabel?: string | null;
  stepLabel?: string | null;
  actionLabel?: string | null;
  trackId?: string | null;
  stepId?: string | null;
  actionId?: string | null;
  onNavigate: (level: HierarchyLevel | 'pathway', nodeId: string | null) => void;
  className?: string;
}

const SEPARATOR = '>';

export function PathwayBreadcrumb({
  pathwayLabel,
  trackLabel,
  stepLabel,
  actionLabel,
  trackId,
  stepId,
  actionId,
  onNavigate,
  className,
}: PathwayBreadcrumbProps) {
  const crumbs: PathwayBreadcrumbCrumb[] = [
    { label: `Pathway: ${pathwayLabel}`, level: 'pathway', nodeId: null },
  ];
  if (trackLabel) {
    crumbs.push({
      label: `Track: ${trackLabel}`,
      level: 'tracks',
      nodeId: trackId ?? null,
    });
  }
  if (stepLabel) {
    crumbs.push({
      label: `Step: ${stepLabel}`,
      level: 'steps',
      nodeId: stepId ?? null,
    });
  }
  if (actionLabel) {
    crumbs.push({
      label: `Action: ${actionLabel}`,
      level: 'actions',
      nodeId: actionId ?? null,
    });
  }

  return (
    <nav
      aria-label="Pathway hierarchy"
      data-testid="pathway-breadcrumb"
      className={[
        'flex items-center gap-1 px-gutter-md py-gutter-sm text-signal-xs text-text-tertiary',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ol className="flex items-center gap-1">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li
              key={`${crumb.level}-${idx}`}
              data-testid={`pathway-breadcrumb-${crumb.level}`}
              className="flex items-center gap-1"
            >
              <button
                type="button"
                aria-current={isLast ? 'location' : undefined}
                disabled={isLast}
                onClick={() => onNavigate(crumb.level, crumb.nodeId ?? null)}
                className={[
                  'rounded-signal-sm px-1 py-0.5 text-signal-xs',
                  isLast
                    ? 'font-semibold text-text-primary'
                    : 'text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {crumb.label}
              </button>
              {!isLast ? (
                <span aria-hidden="true" className="text-text-tertiary">
                  {SEPARATOR}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export { SEPARATOR as PATHWAY_BREADCRUMB_SEPARATOR };
